/**
 * Ledger Help Modal — Explains Payment vs Accrual and channel fee display
 * Owner-directed, conversational copy. Reuses GuideModal pattern.
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function LedgerHelpModal({ onClose, mode = 'payment', onModeChange }) {
  const closeRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const modalContent = (
    <div style={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="ledger-help-title">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 id="ledger-help-title" style={styles.title}>How your Ledger totals work</h2>
          <button
            ref={closeRef}
            type="button"
            style={styles.closeButton}
            onClick={onClose}
            aria-label="Close help modal"
          >
            ×
          </button>
        </div>
        <div style={styles.content}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>By check-in month</h3>
            {onModeChange && (mode === 'payment'
              ? <p style={styles.helperLine}>Current view</p>
              : <button type="button" onClick={() => { onModeChange('payment'); onClose(); }}>View by check-in</button>)}
            <ul style={styles.list}>
              <li>This view shows the full amount you&apos;ll be paid for each reservation.</li>
              <li>The amounts match what you receive as a payout for that stay.</li>
              <li>The date shown is the guest&apos;s check-in date, not the day the money hits your bank.</li>
              <li>Most payouts arrive about 5–7 days after check-in, depending on the booking platform.</li>
            </ul>
            <p style={styles.helperLine}>Use this view to see what you earned from stays that started this month, even if the deposit arrives later.</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Optional report: by nights stayed</h3>
            {onModeChange && (mode === 'accrual'
              ? <p style={styles.helperLine}>Current view</p>
              : <button type="button" onClick={() => { onModeChange('accrual'); onClose(); }}>View by nights</button>)}
            <ul style={styles.list}>
              <li>This view spreads each reservation across the nights of the stay.</li>
              <li>If a stay crosses months, the amounts are split by night.</li>
              <li><strong>Example:</strong> A Jan 28–Feb 2 stay (5 nights) shows 3 nights in January and 2 nights in February.</li>
            </ul>
            <p style={styles.helperLine}>This view is helpful if you want to understand how revenue lines up with when guests actually stayed.</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Channel fees</h3>
            <ul style={styles.list}>
              <li>Some booking platforms charge fees directly to you, and those are shown as a separate line.</li>
              <li>Other platforms deduct their fees before sending your payout.</li>
              <li>Either way, your Owner Revenue total already reflects the correct net amount.</li>
            </ul>
            <p style={styles.helperLine}>If you don&apos;t see a separate channel fee line, it doesn&apos;t mean anything is missing — it just means the platform handled it before payout.</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Why deposits may look different</h3>
            <ul style={styles.list}>
              <li>Deposits usually arrive a few days after check-in.</li>
              <li>Multiple reservations may be combined into one deposit.</li>
              <li>Refunds or adjustments can affect later deposits.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
    overflow: 'auto',
    isolation: 'isolate',
  },
  modal: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: 'calc(100vh - 40px)',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--color-text-primary, #111827)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: 'var(--color-text-secondary, #6b7280)',
    cursor: 'pointer',
    padding: '10px',
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: '1 1 auto',
    scrollbarWidth: 'thin',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-text-primary, #111827)',
    marginBottom: '8px',
  },
  list: {
    fontSize: '14px',
    color: 'var(--color-text-secondary, #374151)',
    lineHeight: '1.7',
    paddingLeft: '20px',
    margin: 0,
  },
  helperLine: {
    fontSize: '14px',
    color: 'var(--color-text-secondary, #6b7280)',
    fontStyle: 'italic',
    lineHeight: '1.6',
    marginTop: '8px',
    marginBottom: 0,
  },
};
